import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import useToast from "../../components/useToast";
import { API_BASE_URL } from "../../config";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardFooter from "../../design-system/components/AppCardFooter";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import { AppStatusChip } from "../../design-system/components/AppChip";
import AppCounter from "../../design-system/components/AppCounter";
import AppDivider from "../../design-system/components/AppDivider";
import AppSelectionList from "../../design-system/components/AppSelectionList";
import AppText from "../../design-system/components/AppText";
import AppPage from "../../design-system/components/AppPage";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { IconWarning } from "../../design-system/icons";
import "../../i18n";
import {
  monthlyPriceForSelection,
  planForEmployeeCount,
  planForKey,
  selectedProductIds,
  SUBSCRIPTION_ADDONS,
  subscriptionViewState,
  TRIAL_DAYS,
  trialDay,
} from "./subscriptionModel";
import "./features.css";

const MAX_EMPLOYEES = 39;

function formatDate(value, language) {
  if (!value) return "–";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "–";
  return new Intl.DateTimeFormat(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatCurrency(value, language) {
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function StatusPanel({ state, subscription, language, onConfigure, t }) {
  const trialEnd = formatDate(subscription.trial_end, language);
  const periodEnd = formatDate(subscription.current_period_end, language);
  const remainingDays = Number(subscription.trial_days_remaining || 0);
  const trialDaysTotal = Number(subscription.trial_days_total || TRIAL_DAYS);
  const currentTrialDay = trialDay(remainingDays, trialDaysTotal);
  const progress = Math.round((currentTrialDay / trialDaysTotal) * 100);

  if (state === "trial") {
    return (
      <AppCard className="subscription-status-card subscription-status-card--trial">
        <AppCardBody>
          <div className="subscription-status-card__content">
            <AppStatusChip status="warning" size="sm">
              {t("feature.redesign.status.trial")}
            </AppStatusChip>
            <AppText as="h2" variant="heading-2" className="subscription-status-card__title">
              {t("feature.redesign.trial.title", { days: remainingDays })}
            </AppText>
            <AppText as="p" variant="body-1" className="subscription-status-card__description">
              {t("feature.redesign.trial.description", { date: trialEnd })}
            </AppText>
            <AppButton variant="primary" onClick={onConfigure}>
              {subscription.subscription_id
                ? t("feature.redesign.actions.change")
                : t("feature.redesign.actions.subscribe")}
            </AppButton>
            <div className="subscription-progress" aria-label={t("feature.redesign.trial.progress", {
              day: currentTrialDay,
              total: trialDaysTotal,
            })}>
              <div className="subscription-progress__labels">
                <span>{t("feature.redesign.trial.progress", { day: currentTrialDay, total: trialDaysTotal })}</span>
                <span>{trialEnd}</span>
              </div>
              <div className="subscription-progress__track">
                <span style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        </AppCardBody>
      </AppCard>
    );
  }

  if (state === "active") {
    return (
      <AppCard className="subscription-status-card subscription-status-card--active">
        <AppCardBody>
          <div className="subscription-status-card__content">
            <AppStatusChip status="success" size="sm">{t("feature.redesign.status.active")}</AppStatusChip>
            <AppText as="h2" variant="heading-2" className="subscription-status-card__title">
              {t("feature.redesign.active.title", { date: periodEnd })}
            </AppText>
            <AppText as="p" variant="body-1" className="subscription-status-card__description">
              {t("feature.redesign.active.description", {
                plan: t(`feature.products.${subscription.plan_key}.name`),
                employees: subscription.employee_limit,
              })}
            </AppText>
            <AppButton variant="outlined" onClick={onConfigure}>
              {t("feature.redesign.actions.change")}
            </AppButton>
          </div>
        </AppCardBody>
      </AppCard>
    );
  }

  if (state === "pending") {
    return (
      <AppCard className="subscription-status-card subscription-status-card--pending">
        <AppCardBody>
          <div className="subscription-status-card__content">
            <AppStatusChip status="warning" size="sm">{t("feature.redesign.status.pending")}</AppStatusChip>
            <AppText as="h2" variant="heading-2" className="subscription-status-card__title">
              {t("feature.redesign.pending.title")}
            </AppText>
            <AppText as="p" variant="body-1" className="subscription-status-card__description">
              {t("feature.redesign.pending.description", {
                dueDate: formatDate(subscription.invoice?.due_date, language),
                periodEnd,
              })}
            </AppText>
            <AppButton variant="outlined" onClick={onConfigure}>
              {t("feature.redesign.actions.change")}
            </AppButton>
          </div>
        </AppCardBody>
      </AppCard>
    );
  }

  return (
    <AppCard className="subscription-status-card subscription-status-card--expired">
      <AppCardBody>
        <div className="subscription-status-card__content">
          <AppStatusChip status="error" size="sm">{t("feature.redesign.status.expired")}</AppStatusChip>
          <AppText as="h2" variant="heading-2" className="subscription-status-card__title">
            {t("feature.redesign.expired.title", { date: trialEnd })}
          </AppText>
          <AppText as="p" variant="body-1" className="subscription-status-card__description">
            {t("feature.redesign.expired.description")}
          </AppText>
          <AppButton variant="primary" onClick={onConfigure}>
            {t("feature.redesign.actions.subscribe")}
          </AppButton>
        </div>
      </AppCardBody>
    </AppCard>
  );
}

function ContractCard({ subscription, language, t }) {
  const monthlyPrice = monthlyPriceForSelection(
    planForKey(subscription.plan_key),
    (subscription.addons || []).map((addon) => addon.addon_key)
  );
  const invoiceTotal = Number(subscription.invoice?.amount_net || 0)
    + Number(subscription.invoice?.amount_tax || 0);

  return (
    <AppCard>
      <AppCardHeader title={t("feature.redesign.contract.title")} />
      <AppCardBody>
        <dl className="subscription-detail-list">
          <div>
            <dt>{t("feature.redesign.contract.period")}</dt>
            <dd>
              {formatDate(subscription.current_period_start, language)} – {formatDate(subscription.current_period_end, language)}
            </dd>
          </div>
          <div>
            <dt>{t("feature.redesign.contract.plan")}</dt>
            <dd>
              {t(`feature.products.${subscription.plan_key}.name`)} · {t("feature.redesign.employees.up_to", {
                count: subscription.employee_limit,
              })}
            </dd>
          </div>
          <div>
            <dt>{t("feature.redesign.contract.addons")}</dt>
            <dd>
              {subscription.addons?.length
                ? subscription.addons.map((addon) => t(`feature.redesign.addons.${addon.addon_key}.name`)).join(", ")
                : t("feature.redesign.contract.no_addons")}
            </dd>
          </div>
          <div>
            <dt>{t("feature.redesign.contract.price")}</dt>
            <dd>{t("feature.redesign.price_per_month", { price: formatCurrency(monthlyPrice, language) })}</dd>
          </div>
          <div>
            <dt>{t("feature.redesign.contract.payment")}</dt>
            <dd>{t("feature.payment.invoice")}</dd>
          </div>
          {subscription.invoice ? (
            <div>
              <dt>{t("feature.redesign.contract.invoice")}</dt>
              <dd>
                {subscription.invoice.invoice_number || "–"} · {formatCurrency(invoiceTotal, language)} · {formatDate(subscription.invoice.due_date, language)}
              </dd>
            </div>
          ) : null}
        </dl>
      </AppCardBody>
    </AppCard>
  );
}

export default function Features() {
  const { t, i18n } = useTranslation();
  const toastOptions = useMemo(() => ({}), []);
  const { success, error } = useToast(toastOptions);
  const configuratorRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscription, setSubscription] = useState({});
  const [employeeCapacity, setEmployeeCapacity] = useState(1);
  const [selectedAddonKeys, setSelectedAddonKeys] = useState([]);
  const [subscriptionMonths, setSubscriptionMonths] = useState(12);
  const [isConfiguring, setIsConfiguring] = useState(true);

  const token = localStorage.getItem("session_token");
  const language = i18n.resolvedLanguage || i18n.language || "de";
  const employeeCount = Number(subscription.employee_count || 0);
  const selectedPlan = planForEmployeeCount(employeeCapacity);
  const monthlyPrice = monthlyPriceForSelection(selectedPlan, selectedAddonKeys);
  const invoiceTotal = monthlyPrice * subscriptionMonths;
  const viewState = subscriptionViewState(subscription);
  const exceedsSupportedCapacity = employeeCount > MAX_EMPLOYEES;

  const addonItems = useMemo(
    () => SUBSCRIPTION_ADDONS.map((addon) => ({
      key: addon.key,
      label: t(`feature.redesign.addons.${addon.key}.name`),
      description: t(`feature.redesign.addons.${addon.key}.description`),
      price: addon.products[selectedPlan.key].monthlyPrice,
    })),
    [selectedPlan.key, t]
  );

  useEffect(() => {
    let mounted = true;

    async function loadSubscription() {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/web/billing/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;

        const nextSubscription = response.data || {};
        const currentPlan = planForKey(nextSubscription.plan_key);
        const initialCapacity = currentPlan?.employeeLimit
          || Math.max(1, Math.min(Number(nextSubscription.employee_count || 1), MAX_EMPLOYEES));

        setSubscription(nextSubscription);
        setEmployeeCapacity(initialCapacity);
        setSelectedAddonKeys((nextSubscription.addons || []).map((addon) => addon.addon_key));
        setSubscriptionMonths(Number(nextSubscription.subscription_months || 12));
        setIsConfiguring(!nextSubscription.subscription_id || !nextSubscription.is_active);
      } catch (requestError) {
        if (mounted) {
          setLoadFailed(true);
          error(requestError?.response?.data?.error || t("feature.redesign.errors.load"));
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    loadSubscription();
    return () => {
      mounted = false;
    };
  }, [error, t, token]);

  function openConfigurator() {
    setIsConfiguring(true);
    window.requestAnimationFrame(() => {
      configuratorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function createInvoiceSubscription() {
    if (exceedsSupportedCapacity) return;
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/web/billing/invoice-subscription`,
        {
          product_ids: selectedProductIds(selectedPlan, selectedAddonKeys),
          subscription_months: subscriptionMonths,
        },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setSubscription((current) => ({
        ...response.data,
        employee_count: response.data.employee_count ?? current.employee_count,
      }));
      setIsConfiguring(false);
      success(t("feature.toast.success"));
    } catch (requestError) {
      error(requestError?.response?.data?.error || t("feature.toast.error"));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AppPage className="features-page">
      <AppWorkspace
        title={t("feature.redesign.title")}
        subtitle={t("feature.redesign.subtitle")}
        loading={isLoading}
        contentClassName="subscription-workspace"
      >
        {loadFailed ? (
          <AppCallout
            variant="error"
            title={t("feature.redesign.errors.load_title")}
            description={t("feature.redesign.errors.load")}
            action={<AppButton variant="outlined" onClick={() => window.location.reload()}>{t("feature.redesign.actions.retry")}</AppButton>}
          />
        ) : (
          <StatusPanel
            state={viewState}
            subscription={subscription}
            language={language}
            onConfigure={openConfigurator}
            t={t}
          />
        )}

        {!loadFailed && subscription.subscription_id && !isConfiguring ? (
          <ContractCard subscription={subscription} language={language} t={t} />
        ) : null}

        {!loadFailed && isConfiguring ? (
          <div ref={configuratorRef} className="subscription-configurator">
            <AppCard>
            <AppCardHeader
              title={t("feature.redesign.configure.title")}
              subtitle={t("feature.redesign.configure.subtitle")}
            />
            <AppCardBody>
              <div className="subscription-configurator__rows">
                <div className="subscription-configurator__row">
                  <div>
                    <AppText as="h3" variant="label-1">{t("feature.redesign.employees.title")}</AppText>
                    <AppText as="p" variant="body-2" tone="secondary">
                      {t("feature.redesign.employees.current", { count: employeeCount })}
                    </AppText>
                  </div>
                  <AppCounter
                    value={employeeCapacity}
                    min={Math.max(1, Math.min(employeeCount, MAX_EMPLOYEES))}
                    max={MAX_EMPLOYEES}
                    onValueChange={setEmployeeCapacity}
                    decrementAriaLabel={t("feature.redesign.employees.decrease")}
                    incrementAriaLabel={t("feature.redesign.employees.increase")}
                  />
                </div>

                <div className="subscription-tier-summary">
                  <div>
                    <AppText as="span" variant="label-1">
                      {t(`feature.products.${selectedPlan.key}.name`)}
                    </AppText>
                    <AppText as="span" variant="body-2" tone="secondary">
                      {t("feature.redesign.employees.up_to", { count: selectedPlan.employeeLimit })}
                    </AppText>
                  </div>
                  <AppText variant="label-1">
                    {t("feature.redesign.price_per_month", {
                      price: formatCurrency(selectedPlan.monthlyPrice, language),
                    })}
                  </AppText>
                </div>

                {exceedsSupportedCapacity ? (
                  <div className="subscription-inline-warning" role="alert">
                    <IconWarning aria-hidden="true" />
                    <span>{t("feature.redesign.employees.unsupported", { count: MAX_EMPLOYEES })}</span>
                  </div>
                ) : null}

                <AppDivider spacing="none" />

                <div className="subscription-section-heading">
                  <AppText as="h3" variant="label-1">{t("feature.redesign.addons.title")}</AppText>
                  <AppText as="p" variant="body-2" tone="secondary">
                    {t("feature.redesign.addons.subtitle")}
                  </AppText>
                </div>
                <AppSelectionList
                  items={addonItems}
                  value={selectedAddonKeys}
                  getItemKey={(item) => item.key}
                  onChange={setSelectedAddonKeys}
                  renderTrailing={(item) => (
                    <AppText variant="label-1">
                      {t("feature.redesign.addons.price", { price: formatCurrency(item.price, language) })}
                    </AppText>
                  )}
                />

                <AppDivider spacing="none" />

                <div className="subscription-configurator__row">
                  <div>
                    <AppText as="h3" variant="label-1">{t("feature.redesign.duration.title")}</AppText>
                    <AppText as="p" variant="body-2" tone="secondary">
                      {t("feature.redesign.duration.subtitle")}
                    </AppText>
                  </div>
                  <AppCounter
                    value={subscriptionMonths}
                    min={1}
                    max={36}
                    onValueChange={setSubscriptionMonths}
                    formatValue={(value) => t("feature.redesign.duration.months", { count: value })}
                    decrementAriaLabel={t("feature.redesign.duration.decrease")}
                    incrementAriaLabel={t("feature.redesign.duration.increase")}
                  />
                </div>
              </div>
            </AppCardBody>
            <AppCardFooter>
              <div className="subscription-checkout">
                <div className="subscription-checkout__price">
                  <AppText variant="body-2" tone="secondary">{t("feature.redesign.total")}</AppText>
                  <AppText as="strong" variant="heading-2">{formatCurrency(invoiceTotal, language)}</AppText>
                  <AppText variant="body-2" tone="secondary">
                    {t("feature.redesign.total_detail", {
                      monthly: formatCurrency(monthlyPrice, language),
                      months: subscriptionMonths,
                    })}
                  </AppText>
                </div>
                <AppButton
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting || exceedsSupportedCapacity}
                  onClick={createInvoiceSubscription}
                >
                  {isSubmitting
                    ? t("feature.redesign.actions.booking")
                    : t("feature.redesign.actions.book_invoice")}
                </AppButton>
              </div>
            </AppCardFooter>
            </AppCard>
          </div>
        ) : null}
      </AppWorkspace>
    </AppPage>
  );
}
